import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Heading } from '../components_catalyst/heading';

export function HomePage() {
  useDocumentTitle('Home', { addSuffix: true });

  const quotes = [
    `A human intelligence system
for the age of artificial intelligence`,
    `Either there is obviously no miscommunication,
or there is no obvious miscommunication.`,
    'Delivery is the strategy.',
    `Past experience is the best predictor 
    of future success.

    Base projections on experience, 
    not estimates.`,
    `Manage for outcomes, 
    not outputs.`,
    `Do more of what works 
    and less of what doesn't,
    quarter after quarter after quarter.`,
    `Build the capacity you need
to deliver the outcomes you want.`,
    `Feel confident knowing 
    what your teams can (and can’t) deliver.`,
    `Have real-time visibility into project status.`,
    `Create the team you need tomorrow
    with the people you have today.`,
    `Continuously improve.`,
    `Work from a single source of truth 
    across teams, organizations, 
    and levels of management.`,
    `Change the systems, not the people.`,
    `Address small problems 
before they become big problems.`,
    `“Don’t talk to me about tickets!”

See the forest beyond the trees.`,
    `Take ‘change nothing’ off the table.`,
    `Don’t try to beat the odds. 
Play a game you can win 80% of the time.`,
    `To get where you want to go
 you have to know where you are.`,
    `Success is not assured by having the right answer. 
You need to have the right owner.`,
    `If they can’t see the finish line, they won’t cross it.`,
  ];

  const [shuffledQuotes, setShuffledQuotes] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const [wasKeyboard, setWasKeyboard] = useState(false);

  // On mount, shuffle the quotes once
  useEffect(() => {
    const shuffled = [...quotes].sort(() => Math.random() - 0.5);
    setShuffledQuotes(shuffled);
  }, []);

  const showNextQuote = (fromKeyboard = false) => {
    setWasKeyboard(fromKeyboard);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % shuffledQuotes.length);
  };

  return (
    <section className="space-y-8" aria-label="Homepage quotes">
      <Heading level={1} className="sr-only">
        MilestoneMap.org Home
      </Heading>
      <div className="flex justify-center items-center min-h-[400px]">
        <AnimatePresence mode="wait">
          {currentIndex >= 0 && (
            <motion.div
              ref={cardRef}
              key={currentIndex}
              onClick={() => showNextQuote(false)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  showNextQuote(true);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Current quote: ${shuffledQuotes[currentIndex]}. Click or press Enter to show next quote.`}
              className="relative w-[600px] h-[300px] rounded-lg bg-blue-600 cursor-pointer flex items-center justify-center p-8 focus:outline-none focus:ring-4 focus:ring-blue-800 focus:ring-offset-4 focus:ring-offset-white"
              // Enter animation
              initial={{
                opacity: 0,
                y: 50,
                scale: 0.3,
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              // Exit animation
              exit={{
                opacity: 0,
                scale: 0,
                rotate: 10,
              }}
              transition={{
                duration: 0.5,
                ease: [0.32, 0.72, 0, 1],
              }}
              whileHover={{
                scale: 1.02,
                transition: { duration: 0.2 },
              }}
              onAnimationComplete={() => wasKeyboard && cardRef.current?.focus()}
            >
              <motion.div
                className="text-white text-2xl font-bold text-center whitespace-pre-line"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {shuffledQuotes[currentIndex]}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
